import { ActionIcon, Alert, Button, Group, Loader, Menu, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Focus,
  Images,
  Library,
  Minus,
  Package,
  Plus,
  Trash2,
  Type,
  WandSparkles,
} from "lucide-react";
import type { ReactNode } from "react";

import type { Job } from "../../../api/generated/model";
import { UploadState, type ImageUpload } from "../../../platform/media/image-upload";
import { Soon } from "../../../ui/Soon";
import type { EditResult } from "./edits";
import type { Notice } from "./errors";
import {
  canRestack,
  centered,
  MAX_SCALE,
  MIN_SCALE,
  removed,
  restacked,
  scaled,
  SCALE_STEP,
  SOURCE_LABEL,
  visible,
  type Composition,
  type EditorLayer,
  type PictureLayer,
  type TextLayer,
} from "./model";
import type { PictureSource } from "./LayerPickers";
import { textValuesOf, type TextValues } from "./text";
import { TextControls } from "./TextControls";
import classes from "./layer-editor.module.css";

export interface LayersRailProps {
  composition: Composition;
  selected: string | null;
  /** A picture being added: "Add layer" waits. */
  adding: boolean;
  /** While a save runs: a layer can be selected, not changed, as live's. */
  locked: boolean;
  /** Live's words for what last failed or a save done, until dismissed or the next action. */
  notice: Notice | null;
  onDismissNotice: () => void;
  /** The saved image's upload: its progress, and why it failed. */
  upload: ImageUpload;
  /** A row chosen: the layer is selected, and its button on the canvas takes the focus. */
  onSelect: (id: string) => void;
  onCommit: (next: Composition) => void;
  /** One of "Add layer"'s picture sources chosen: its picker opens. */
  onPick: (source: PictureSource) => void;
  /** "Add layer"'s "Text": live's new text on top. */
  onAddText: () => void;
  /** A text's values as last asked for, until the layer has them. */
  pendingText: { id: string; values: TextValues } | null;
  /** Whether "Edit with AI" works for the team; else it is Coming soon (decision 2). */
  aiEdits: boolean;
  /** The Layerize's AI edits: running, and finished ones not in the composition. */
  edits: { running: Job[]; unapplied: { job: Job; result: EditResult }[] };
  /** The layers an edit started here is running on. */
  busy: Set<string>;
  /** Live's "Edit with AI" on a picture layer: its dialog opens. */
  onEditWithAI: (layer: PictureLayer) => void;
  /** Live's "Add as layer" on a finished edit. */
  onAddEdit: (result: EditResult) => void;
  onChangeText: (layer: TextLayer, values: TextValues) => void;
}

function Thumb({ layer }: { layer: EditorLayer }) {
  return layer.kind === "picture" ? (
    <img src={layer.url} alt="" className={classes.thumb} />
  ) : (
    <span className={classes.thumb} aria-hidden>
      Aa
    </span>
  );
}

interface AddLayerMenuProps {
  adding: boolean;
  locked: boolean;
  onPick: (source: PictureSource) => void;
  onAddText: () => void;
}

/** Live's "Add layer" menu: "Text", then the "Image source" pickers. */
function AddLayerMenu({ adding, locked, onPick, onAddText }: AddLayerMenuProps) {
  return (
    <Menu position="bottom-end">
      <Menu.Target>
        <Button
          size="compact-xs"
          variant="light"
          leftSection={<Plus size={13} />}
          rightSection={<ChevronDown size={12} />}
          loading={adding}
          disabled={locked}
        >
          Add layer
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item leftSection={<Type size={14} />} onClick={onAddText}>
          Text
        </Menu.Item>
        <Menu.Divider />
        <Menu.Label>Image source</Menu.Label>
        <Menu.Item leftSection={<Package size={14} />} onClick={() => onPick("product")}>
          Product images
        </Menu.Item>
        <Menu.Item leftSection={<Images size={14} />} onClick={() => onPick("generation")}>
          My Generations
        </Menu.Item>
        <Menu.Item leftSection={<Library size={14} />} onClick={() => onPick("library")}>
          Library or upload
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

/**
 * Live's rail (spec G13b; `layers-rail`): "Layers", "Top layer first", "Add layer", a row per
 * visible layer top first (its picture, name and source), and the inspector of the selected
 * layer: center, forward, backward, delete, "Edit with AI" and the scale, 25 to 200 % by 10.
 */
export function LayersRail(props: LayersRailProps) {
  const { composition, selected, adding, locked, notice, onDismissNotice, upload, onSelect, onCommit, onPick } = props;
  const { onAddText, onChangeText, pendingText, aiEdits, edits, busy, onEditWithAI, onAddEdit } = props;
  const rows = [...visible(composition)].reverse();
  const layer = selected === null ? undefined : composition.layers[selected];
  return (
    <aside className={classes.rail} aria-label="Layers">
      <Group justify="space-between" wrap="nowrap">
        <div>
          <Text fw={500}>Layers</Text>
          <Text size="xs" c="var(--cai-color-text-muted)">
            Top layer first
          </Text>
        </div>
        <AddLayerMenu adding={adding} locked={locked} onPick={onPick} onAddText={onAddText} />
      </Group>
      {notice === null ? null : (
        <Alert
          color={notice.tone === "error" ? "red" : "green"}
          variant="light"
          role={notice.tone === "error" ? "alert" : "status"}
          withCloseButton
          closeButtonLabel="Dismiss"
          onClose={onDismissNotice}
        >
          <Text size="sm">{notice.words}</Text>
        </Alert>
      )}
      <UploadState upload={upload} />
      <Stack gap={4} className={classes.rows}>
        {rows.map((id) => {
          const each = composition.layers[id]!;
          return (
            <UnstyledButton
              key={id}
              className={classes.row}
              aria-label={`Select ${each.name}`}
              aria-pressed={id === selected}
              onClick={() => onSelect(id)}
            >
              <Thumb layer={each} />
              <span className={classes.rowText}>
                <Text size="sm" truncate>
                  {each.name}
                </Text>
                <Text size="xs" c="var(--cai-color-text-muted)">
                  {SOURCE_LABEL[each.source]}
                </Text>
              </span>
              {busy.has(id) ? <Loader size={12} aria-label="AI edit in progress" className={classes.rowBusy} /> : null}
            </UnstyledButton>
          );
        })}
      </Stack>
      {layer === undefined ? null : (
        <Inspector
          composition={composition}
          layer={layer}
          locked={locked}
          onCommit={onCommit}
          pendingText={pendingText}
          onChangeText={onChangeText}
          aiEdits={aiEdits}
          onEditWithAI={onEditWithAI}
        />
      )}
      {/* After the inspector, as live's rail. */}
      <AiEdits edits={edits} locked={locked} onAddEdit={onAddEdit} />
    </aside>
  );
}

/** Live's "AI edits": the ones running (a spinner and the prompt), and finished ones not in the
 * composition (their picture, the prompt and "Add as layer"). None, no panel. */
function AiEdits(props: {
  edits: LayersRailProps["edits"];
  locked: boolean;
  onAddEdit: (result: EditResult) => void;
}) {
  const { edits, locked, onAddEdit } = props;
  if (edits.running.length === 0 && edits.unapplied.length === 0) return null;
  return (
    <Stack gap={6} className={classes.inspector} aria-label="AI edits">
      <Text size="sm" fw={500}>
        AI edits
      </Text>
      {edits.running.map((job) => (
        <Group key={job.id} gap={8} wrap="nowrap">
          <Loader size={12} aria-label="Running" />
          <Text size="xs" lineClamp={1}>
            {job.params?.prompt}
          </Text>
        </Group>
      ))}
      {edits.unapplied.map(({ job, result }) => (
        <Group key={job.id} gap={8} wrap="nowrap">
          <img src={result.url} alt="" className={classes.thumb} />
          <Text size="xs" lineClamp={1} className={classes.grow}>
            {job.params?.prompt}
          </Text>
          <Button size="compact-xs" variant="light" disabled={locked} onClick={() => onAddEdit(result)}>
            Add as layer
          </Button>
        </Group>
      ))}
    </Stack>
  );
}

function Inspector(props: {
  composition: Composition;
  layer: EditorLayer;
  locked: boolean;
  onCommit: (next: Composition) => void;
  pendingText: { id: string; values: TextValues } | null;
  onChangeText: (layer: TextLayer, values: TextValues) => void;
  aiEdits: boolean;
  onEditWithAI: (layer: PictureLayer) => void;
}) {
  const { composition, layer, locked, onCommit, pendingText, onChangeText, aiEdits, onEditWithAI } = props;
  const { id, name } = layer;
  const scale = composition.transforms[id]!.scale;
  const action = (tip: string, label: string, icon: ReactNode, next: () => Composition, disabled = false) => (
    <Tooltip label={tip}>
      <ActionIcon variant="subtle" aria-label={label} disabled={disabled || locked} onClick={() => onCommit(next())}>
        {icon}
      </ActionIcon>
    </Tooltip>
  );
  // Live's scale steps: 10 % from where it is, rounded to the step, within 25 to 200 %.
  const stepped = (direction: 1 | -1) =>
    scaled(composition, id, Math.round((scale + direction * SCALE_STEP) * 10) / 10);
  return (
    <Stack gap="xs" className={classes.inspector} aria-label={`${name} settings`}>
      <Group gap="sm" wrap="nowrap">
        <Thumb layer={layer} />
        <span className={classes.rowText}>
          <Text size="sm" truncate>
            {name}
          </Text>
          <Text size="xs" c="var(--cai-color-text-muted)">
            {SOURCE_LABEL[layer.source]}
          </Text>
        </span>
      </Group>
      <Group gap={4} wrap="nowrap">
        {action("Center on canvas", `Center ${name} on canvas`, <Focus size={16} />, () => centered(composition, id))}
        {action("Bring forward", `Bring ${name} forward`, <ArrowUp size={16} />, () => restacked(composition, id, 1), !canRestack(composition, id, 1))}
        {action("Send backward", `Send ${name} backward`, <ArrowDown size={16} />, () => restacked(composition, id, -1), !canRestack(composition, id, -1))}
        {action("Delete layer", `Delete ${name}`, <Trash2 size={16} />, () => removed(composition, id))}
      </Group>
      {layer.kind === "picture" && aiEdits ? (
        <Button
          size="xs"
          variant="light"
          leftSection={<WandSparkles size={14} />}
          disabled={locked}
          onClick={() => onEditWithAI(layer)}
        >
          Edit with AI
        </Button>
      ) : layer.kind === "picture" ? (
        // Where the catalog does not offer the edit to the team yet (decision 2).
        <Soon>
          {(soonProps) => (
            <Button {...soonProps} size="xs" variant="light" leftSection={<WandSparkles size={14} />}>
              Edit with AI
            </Button>
          )}
        </Soon>
      ) : (
        <TextControls
          layer={layer}
          locked={locked}
          values={pendingText?.id === layer.id ? pendingText.values : textValuesOf(layer)}
          onChange={onChangeText}
        />
      )}
      <Group justify="space-between" wrap="nowrap">
        <Text size="sm">Scale</Text>
        <Group gap={4} wrap="nowrap">
          <ActionIcon
            variant="subtle"
            aria-label="Decrease layer scale"
            disabled={scale <= MIN_SCALE || locked}
            onClick={() => onCommit(stepped(-1))}
          >
            <Minus size={14} />
          </ActionIcon>
          <Text size="sm" w={44} ta="center">
            {Math.round(scale * 100)}%
          </Text>
          <ActionIcon
            variant="subtle"
            aria-label="Increase layer scale"
            disabled={scale >= MAX_SCALE || locked}
            onClick={() => onCommit(stepped(1))}
          >
            <Plus size={14} />
          </ActionIcon>
        </Group>
      </Group>
    </Stack>
  );
}
